import { useCallback, useEffect, useRef } from "react";
import { parseAamvaBarcode, type AamvaBarcode } from "../utils/aamva/aamva";
import { useIdScanSession, type UseIdScanSessionReturn } from "./useIdScanSession";
import { useBarcodeScanner } from "./useBarcodeScanner";
import {
  decodePdf417,
  isPdf417ReaderReady,
  pdf417ReaderMode,
  pdf417ReaderUnavailableReason,
  loadPhoto,
  photoTasks,
  PHOTO_READ_BUDGET_MS,
} from "../services/scan/pdf417Reader";
import { grabFullResolutionStill, type StillSource } from "../services/scan/cameraTuning";

// EN-4743. The back of a driver's licence or state ID: the PDF417 is read live
// during the countdown (see useIdScanSession for the flow). After the last miss
// the card is read once more from the best picture available, like the kiosk's
// full-resolution photo pass (visitly-kiosk-app src/utils/photoBarcode.js):
// the camera's full-resolution still where the browser can take one, else the
// full-resolution video frame, else the photo JPEG.
const BARCODE_EVENTS = {
  read: "id_barcode_read",
  attemptFailed: "id_barcode_attempt_failed",
  fallbackSubmit: "id_barcode_fallback_submit",
  unavailable: "id_barcode_unavailable",
  rejected: "id_barcode_rejected",
  summary: "id_scan_summary",
};

type Still = { image: ImageBitmap; source: StillSource };
// How long to wait for the camera's full-resolution still before using the video
// frame instead (instant). Keeps the last-chance read from holding up the send.
const STILL_TIMEOUT_MS = 1500;
export type PhotoReadSource = StillSource | "photo-jpeg";

/**
 * Reads the barcode from the last-chance picture: `still` if there is one,
 * otherwise the photo JPEG. Frees the still. Reports which picture was read.
 */
export const readBarcodeFromPhoto = async (
  photo: string | null,
  still: Still | null,
): Promise<{ barcode: AamvaBarcode | null; source: PhotoReadSource | null }> => {
  try {
    let source: PhotoReadSource | null = null;
    let image: CanvasImageSource | null = null;
    let width = 0;
    let height = 0;
    if (still) {
      ({ image } = still);
      source = still.source;
      width = still.image.width;
      height = still.image.height;
    } else if (photo) {
      const img = await loadPhoto(photo);
      if (img) {
        image = img;
        source = "photo-jpeg";
        width = img.naturalWidth;
        height = img.naturalHeight;
      }
    }
    if (!image || !width || !height) return { barcode: null, source };
    // Bounded (PHOTO_READ_BUDGET_MS), most useful tries first, so the send is never
    // held up for long by a card that won't read.
    const { text } = await decodePdf417(image, photoTasks(width, height), PHOTO_READ_BUDGET_MS);
    return { barcode: parseAamvaBarcode(text), source };
  } finally {
    still?.image.close();
  }
};

/** What the page knows about the camera, for the summary row. */
export type CameraInfo = Record<string, unknown>;

interface UseBarcodeScanSessionOptions {
  enabled: boolean;
  videoEl: HTMLVideoElement | null | undefined;
  stageEl: Element | null;
  guideEl: Element | null;
  // Takes the back photo, the same way the Capture button does, and returns it.
  takePhoto: () => string | null;
  // Sends the photos for validation without the visitor tapping Continue.
  autoSubmit: () => void;
  // Camera settings actually in use (resolution, zoom, focus, torch).
  getCameraInfo?: () => CameraInfo;
}

type UseBarcodeScanSessionReturn = Omit<UseIdScanSessionReturn<AamvaBarcode>, "data"> & {
  barcode: AamvaBarcode | null;
};

export const useBarcodeScanSession = (options: UseBarcodeScanSessionOptions): UseBarcodeScanSessionReturn => {
  const { videoEl, getCameraInfo } = options;
  const videoRef = useRef(videoEl);
  const cameraInfoRef = useRef(getCameraInfo);
  useEffect(() => {
    videoRef.current = videoEl;
    cameraInfoRef.current = getCameraInfo;
  }, [videoEl, getCameraInfo]);
  const photoSourceRef = useRef<PhotoReadSource | null>(null);

  const grabStill = useCallback(() => grabFullResolutionStill(videoRef.current, STILL_TIMEOUT_MS), []);
  const readPhoto = useCallback(async (photo: string | null, still: Still | null) => {
    const { barcode, source } = await readBarcodeFromPhoto(photo, still);
    photoSourceRef.current = source;
    return barcode;
  }, []);
  const releaseStill = useCallback((still: Still) => still.image.close(), []);
  const getSummary = useCallback(
    () => ({
      readerMode: pdf417ReaderMode(),
      photoSource: photoSourceRef.current,
      ...(cameraInfoRef.current?.() ?? {}),
    }),
    [],
  );

  const { data, ...session } = useIdScanSession<AamvaBarcode, Still>({
    ...options,
    useScanner: useBarcodeScanner,
    isReaderReady: isPdf417ReaderReady,
    readerUnavailableReason: pdf417ReaderUnavailableReason,
    grabStill,
    readPhoto,
    releaseStill,
    getSummary,
    events: BARCODE_EVENTS,
  });
  return { ...session, barcode: data };
};
